import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { name: "", slug: "", bio_it: "", bio_en: "", image_url: "", instagram: "", spotify: "", youtube: "", genre: "", order: 0, active: true };

export default function AdminArtists() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/admin/artists").then((r) => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/artists/${editing.id}`, editing);
        toast.success("Artista aggiornato");
      } else {
        await api.post("/admin/artists", editing);
        toast.success("Artista creato");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Confermi l'eliminazione?")) return;
    await api.delete(`/admin/artists/${id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-artists-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Roster</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Artisti</h1>
        </div>
        <button className="btn-cta" onClick={() => setEditing({ ...EMPTY })} data-testid="admin-artist-add">
          <Plus size={16} /> Nuovo artista
        </button>
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/50">Caricamento…</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7]" data-testid={`admin-artist-${a.slug}`}>
              <div className="aspect-video bg-[#F2F2EC] overflow-hidden">
                {a.image_url && <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl uppercase">{a.name}</h3>
                  {!a.active && <span className="text-[10px] uppercase text-[#0A0A0A]/40">Nascosto</span>}
                </div>
                <div className="text-xs uppercase tracking-widest text-[#FF5A1F]">{a.genre}</div>
                <p className="mt-2 text-sm text-[#0A0A0A]/70 line-clamp-3">{a.bio_it}</p>
                <div className="mt-4 flex gap-2">
                  <button className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-[#FAFAF7] flex items-center gap-1" onClick={() => setEditing(a)} data-testid={`admin-artist-edit-${a.slug}`}>
                    <Pencil size={12} /> Modifica
                  </button>
                  <button className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest hover:bg-red-600 hover:text-white hover:border-red-600 flex items-center gap-1" onClick={() => remove(a.id)} data-testid={`admin-artist-delete-${a.slug}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ArtistEditor value={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

const ArtistEditor = ({ value, onChange, onSave, onClose }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const fileInputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  const processFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Serve un file immagine (JPG, PNG, WebP)");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande — max 5 MB");
      return;
    }
    setUploading(true);
    // Compress large images client-side (>800KB) to stay light in DB
    if (f.size > 800 * 1024) {
      try {
        const bitmap = await createImageBitmap(f);
        const targetW = Math.min(bitmap.width, 1200);
        const scale = targetW / bitmap.width;
        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = Math.round(bitmap.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
        const reader = new FileReader();
        reader.onload = () => { set("image_url", reader.result); setUploading(false); toast.success("Immagine ottimizzata e caricata"); };
        reader.readAsDataURL(blob);
        return;
      } catch { /* fall through to raw upload */ }
    }
    const reader = new FileReader();
    reader.onload = () => { set("image_url", reader.result); setUploading(false); toast.success("Immagine caricata"); };
    reader.onerror = () => { setUploading(false); toast.error("Errore caricamento"); };
    reader.readAsDataURL(f);
  };

  const uploadImg = (e) => processFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-artist-editor">
      <div className="bg-[#FAFAF7] max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex items-center justify-between">
          <h3 className="font-display text-2xl uppercase">{value.id ? "Modifica artista" : "Nuovo artista"}</h3>
          <button onClick={onClose} data-testid="admin-artist-editor-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Nome" value={value.name} onChange={(v) => set("name", v)} testId="ed-name" />
            <Input label="Slug (URL)" value={value.slug} onChange={(v) => set("slug", v)} testId="ed-slug" placeholder="auto se vuoto" />
            <Input label="Genere" value={value.genre} onChange={(v) => set("genre", v)} testId="ed-genre" />
            <Input label="Ordine" type="number" value={value.order} onChange={(v) => set("order", parseInt(v) || 0)} testId="ed-order" />
          </div>
          <TextArea label="Bio (IT)" value={value.bio_it} onChange={(v) => set("bio_it", v)} testId="ed-bio-it" />
          <TextArea label="Bio (EN)" value={value.bio_en} onChange={(v) => set("bio_en", v)} testId="ed-bio-en" />
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Instagram" value={value.instagram} onChange={(v) => set("instagram", v)} testId="ed-ig" />
            <Input label="Spotify" value={value.spotify} onChange={(v) => set("spotify", v)} testId="ed-spotify" />
            <Input label="YouTube" value={value.youtube} onChange={(v) => set("youtube", v)} testId="ed-yt" />
          </div>
          <div data-testid="ed-image-block">
            <label className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60 flex items-center gap-2">
              <Upload size={13} /> Foto dell'artista
            </label>

            {/* Big drag & drop zone */}
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              className={`mt-2 border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#FF5A1F] bg-[#FF5A1F]/5" : "border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:bg-[#FF5A1F]/5"}`}
              data-testid="ed-image-drop-zone"
            >
              {value.image_url ? (
                <div className="flex items-center gap-4 flex-wrap justify-center">
                  <img src={value.image_url} alt="preview" className="h-40 w-40 object-cover border-2 border-[#0A0A0A]" />
                  <div className="text-left">
                    <div className="text-sm font-bold text-emerald-700">✓ Immagine caricata</div>
                    <div className="text-xs text-[#0A0A0A]/60 mt-1">Clicca o trascina per sostituirla</div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); set("image_url", ""); }}
                      className="mt-2 text-xs text-red-600 hover:underline uppercase tracking-widest font-bold"
                      data-testid="ed-image-remove"
                    >Rimuovi</button>
                  </div>
                </div>
              ) : uploading ? (
                <div className="py-6 text-[#0A0A0A]/60">Caricamento…</div>
              ) : (
                <div className="py-6">
                  <Upload size={40} className="mx-auto text-[#FF5A1F]" strokeWidth={1.5} />
                  <div className="mt-3 font-bold text-lg">Trascina qui la foto</div>
                  <div className="mt-1 text-xs text-[#0A0A0A]/60">oppure clicca per selezionarla dal computer</div>
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-[#0A0A0A]/40">JPG, PNG o WebP · max 5 MB</div>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={uploadImg} data-testid="ed-image-file" />
            </div>

            {/* Alternative: paste URL */}
            <details className="mt-2">
              <summary className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/40 cursor-pointer hover:text-[#0A0A0A]/70">Oppure incolla un URL esterno</summary>
              <input
                type="text"
                value={value.image_url?.startsWith("data:") ? "" : (value.image_url || "")}
                onChange={(e) => set("image_url", e.target.value)}
                placeholder="https://..."
                className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 text-sm font-mono"
                data-testid="ed-image-url"
              />
            </details>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={value.active} onChange={(e) => set("active", e.target.checked)} data-testid="ed-active" />
            <span className="text-sm">Visibile sul sito</span>
          </label>
        </div>
        <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline-dark" data-testid="admin-artist-cancel">Annulla</button>
          <button onClick={onSave} className="btn-cta" data-testid="admin-artist-save">Salva</button>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", testId, placeholder }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white" />
  </label>
);

const TextArea = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId} className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white resize-none" />
  </label>
);
